// Top-level App. Per v2.1 §10.3 the frontend hosts FOUR UIs in ONE
// package — they share components and an API client but route to
// distinct entry pages.
//
// IA (frontend redesign v0.2, R1 + D8): the surfaces hang off the
// Lattice as the primary object — four flat surfaces: "Build" (author a
// Lattice, was Workbench), "Manage" (the registry/lifecycle list, was
// Management/"Collections"), "Query" (Frame-QL) and "Analyze" (the
// agent). "AC" / "Analytic Collection" remains the formal/code term
// (the API still speaks ac_name); "Lattice" is the user-facing noun only
// (D8 supersedes D7's "Cube", which superseded D1's "Collection").
//
// Routes (unchanged in R1 — route restructure is R2):
//   /workbench/*   — Build: the authoring UI
//   /management/*  — Collections: the installation + lifecycle UI
//   /query/*       — Explore › Query: the Frame-QL query UI
//   /analyst/*     — Explore › Analyst: the AI agent surface (A5).
//   /              — redirect to /workbench (Collections-as-home lands in
//                    R2 once Management no longer requires an open session)

import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, Route, Routes } from "react-router-dom";
import { AnalystPage } from "./analyst/AnalystPage";
import { type ConditionsResponse, type InstallationResponse } from "./api/client";
import { DocsPage } from "./docs/DocsPage";
import { ManagementPage } from "./management/ManagementPage";
import { QueryPage } from "./query/QueryPage";
import { SessionProvider, useSession } from "./workbench/SessionContext";
import { WorkbenchLayout } from "./workbench/WorkbenchLayout";

export function App() {
  return (
    // SessionProvider lifted to the app root so Build AND Collections
    // (and any future surface) share the active session — the user opens
    // a session in Build, then can register / fork it from Collections
    // without having to "reopen" anywhere.
    <SessionProvider>
      <div style={appShellStyle}>
        <header style={headerStyle}>
          <span style={brandStyle}>Coframe Lattice</span>
          <nav style={navStyle}>
            <Link to="/workbench" style={linkStyle}>
              Build
            </Link>
            <Link to="/management" style={linkStyle}>
              Manage
            </Link>
            <Link to="/query" style={linkStyle}>
              Query
            </Link>
            <Link to="/analyst" style={linkStyle}>
              Analyze
            </Link>
            <Link to="/docs" style={linkStyle}>
              Docs
            </Link>
          </nav>
          <CollectionChip />
        </header>
        <main style={mainStyle}>
          <Routes>
            <Route path="/" element={<Navigate to="/workbench" replace />} />
            <Route path="/workbench/*" element={<WorkbenchLayout />} />
            <Route path="/management/*" element={<ManagementPage />} />
            <Route path="/query/*" element={<QueryPage />} />
            <Route path="/analyst/*" element={<AnalystPage />} />
            <Route path="/docs/*" element={<DocsPage />} />
          </Routes>
        </main>
      </div>
    </SessionProvider>
  );
}

// ── Collection chip (frontend redesign v0.2 §5) ──────────────────────
// Shows the current Collection + installation + filter scope AND the
// rolled-up four-state certificate, so "is this trustworthy?" is
// answerable from any screen. Renders nothing when there's no session.
// Reads the installation + conditions endpoints so it stays accurate as
// Collections are registered / forked / re-certified.

/** Roll the four-state certificate up to a single headline for the chip.
 *  Any CONTRADICTED dominates (with a hard lock when fail_closed); then
 *  UNTESTABLE; then all-VERIFIED; else CORROBORATED. A/AA/AAA is NOT the
 *  headline — it stays a derived secondary view (invariant #3). */
interface CertHeadline {
  glyph: string;
  label: string;
  color: string;
  bg: string;
  border: string;
  title: string;
}

function rollupCertificate(
  cert: ConditionsResponse["certificate"],
): CertHeadline | null {
  if (cert === undefined || cert === null) return null;
  const values = Object.values(cert.states);
  if (values.length === 0) {
    return {
      glyph: "—",
      label: "not certified",
      color: "#777",
      bg: "#f2f2f2",
      border: "#ddd",
      title: "No integrity conditions assessed yet",
    };
  }
  if (values.some((v) => v === "CONTRADICTED")) {
    return {
      glyph: cert.fail_closed ? "⛔" : "✕",
      label: "CONTRADICTED",
      color: "#a01b1b",
      bg: "#fdecec",
      border: "#f3b6b6",
      title: cert.fail_closed
        ? `Fail-closed — blocking condition(s) contradicted: ${cert.blocking_contradictions.join(", ")}`
        : "A condition is contradicted (non-blocking)",
    };
  }
  if (values.some((v) => v === "UNTESTABLE")) {
    return {
      glyph: "⚠",
      label: "UNTESTABLE",
      color: "#8a5b00",
      bg: "#fff5d6",
      border: "#e6d8a8",
      title: "A condition could not be tested — its absence is never certified",
    };
  }
  if (values.every((v) => v === "VERIFIED")) {
    return {
      glyph: "✅",
      label: "VERIFIED",
      color: "#1a6b3a",
      bg: "#e8f5ee",
      border: "#b3dfc4",
      title: "Every assessed condition is data-verified",
    };
  }
  return {
    glyph: "◐",
    label: "CORROBORATED",
    color: "#0f6b80",
    bg: "#e4f1f5",
    border: "#a9d4de",
    title: "Assessed conditions are corroborated (some by assertion, not data-verified)",
  };
}

function CollectionChip() {
  const { client, session } = useSession();
  const [info, setInfo] = useState<InstallationResponse | null>(null);
  const [conditions, setConditions] = useState<ConditionsResponse | null>(null);

  const refresh = useCallback(async () => {
    if (session === null) return;
    try {
      const r = await client.getInstallation(session.session_id);
      setInfo(r);
    } catch {
      // Endpoint failing shouldn't break the rest of the UI.
      setInfo(null);
    }
    try {
      const c = await client.getConditions(session.session_id);
      setConditions(c);
    } catch {
      setConditions(null);
    }
  }, [client, session]);

  useEffect(() => {
    setInfo(null);
    setConditions(null);
    if (session !== null) refresh();
  }, [session, refresh]);

  if (session === null) return null;
  if (info === null) return null;

  // Choose the Collection name to display. Priority:
  //   1. session.ac_name — what the user typed on create. Canonical.
  //   2. session's draft_ac.ac_name — once InitializeAC runs, this may
  //      diverge from session.ac_name (e.g., user renamed).
  //   3. first registered Collection of the installation — fallback.
  const activeName: string =
    session.ac_name ||
    info.draft_ac_name ||
    (info.acs.length > 0 ? info.acs[0].name : "(no Lattice)");
  const filterCount =
    (info.draft_ac_filter?.length ?? 0) > 0
      ? (info.draft_ac_filter?.length ?? 0)
      : (info.acs[0]?.ac_filter?.length ?? 0);
  const acCount = info.acs.length;
  const headline = rollupCertificate(conditions?.certificate);

  return (
    <div style={chipWrapperStyle}>
      <div style={chipLineStyle}>
        <span style={chipLabelStyle}>data source</span>
        <code style={chipValueStyle}>{info.installation_id}</code>
      </div>
      <div style={chipLineStyle}>
        <span style={chipLabelStyle}>lattice</span>
        <code style={chipValueStyle}>{activeName}</code>
        {headline !== null && (
          <span
            style={certChipStyle(headline)}
            title={headline.title}
          >
            <span aria-hidden>{headline.glyph}</span> {headline.label}
          </span>
        )}
        {acCount > 1 && (
          <span style={chipBadgeStyle}>{acCount} lattices on this data source</span>
        )}
        {filterCount > 0 && (
          <span
            style={chipFilterBadgeStyle}
            title={`Lattice scoped by ${filterCount} filter constraint(s) — queries are auto-narrowed`}
          >
            filtered ({filterCount})
          </span>
        )}
      </div>
    </div>
  );
}

function certChipStyle(h: CertHeadline): React.CSSProperties {
  return {
    fontSize: "0.65rem",
    fontWeight: 600,
    letterSpacing: "0.03em",
    color: h.color,
    background: h.bg,
    border: `1px solid ${h.border}`,
    borderRadius: 3,
    padding: "0.05rem 0.4rem",
  };
}

const chipWrapperStyle: React.CSSProperties = {
  marginLeft: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "0.15rem",
  fontSize: "0.75rem",
  color: "#444",
};
const chipLineStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.45rem",
};
const chipLabelStyle: React.CSSProperties = {
  color: "#888",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  fontSize: "0.65rem",
};
const chipValueStyle: React.CSSProperties = {
  fontSize: "0.8rem",
  color: "#222",
};
const chipBadgeStyle: React.CSSProperties = {
  fontSize: "0.65rem",
  color: "#0a6",
  background: "#e8f5ee",
  border: "1px solid #b3dfc4",
  borderRadius: 3,
  padding: "0.05rem 0.35rem",
};
const chipFilterBadgeStyle: React.CSSProperties = {
  fontSize: "0.65rem",
  color: "#8a5b00",
  background: "#fff5d6",
  border: "1px solid #e6d8a8",
  borderRadius: 3,
  padding: "0.05rem 0.35rem",
};

// ── Inline styles — intentionally minimal for slice 2. ─────────────────
// A real design system lands in a follow-on slice; slice 2 just needs
// the layout to be navigable and the proof-of-life to be visible.

const appShellStyle: React.CSSProperties = {
  fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  color: "#222",
  margin: 0,
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: "1.5rem",
  padding: "0.75rem 1.5rem",
  borderBottom: "1px solid #ddd",
  background: "#fafafa",
};

const brandStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: "1.1rem",
};

const navStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: "1rem",
};

// D8: the nav is four flat surfaces over the Lattice object
// (Build · Manage · Query · Analyze), plus a standalone "Docs" entry
// (canonical-document downloads — not a Lattice surface). The earlier
// "Explore" grouping of Query + Analyst was dropped when the IA
// flattened — re-add a navGroup style here if it comes back.

const linkStyle: React.CSSProperties = {
  color: "#0366d6",
  textDecoration: "none",
};

const mainStyle: React.CSSProperties = {
  padding: "1.5rem",
  flex: 1,
};
